<script setup>
/**
 * "Signing in" (XN-4561): the password, by an emailed link — the call the
 * staff record's "Send a password reset link" makes — the extra sign-in
 * check, which nothing offers yet (story AC 6 waits on SSO), and Sign out.
 * Nothing stores when a password last changed, so the row does not say.
 * Prototype `profile()`, build 30 Sep 12:40.
 */
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import XlButton from '../xl/XlButton.vue';
import XlTag from '../xl/XlTag.vue';
import { sendPasswordReset } from '../../api/policyholders';

const props = defineProps({
  email: { type: String, required: true },
});
const router = useRouter();

// "grace@example.com" reads "g•••@example.com", as the hub's Mask::email writes it.
const masked = (email) => {
  const at = email.lastIndexOf('@');
  return at > 0 ? `${email[0]}•••${email.slice(at)}` : '•••';
};

// The row's own line says each outcome; it is a live region from the start.
const reset = ref('idle'); // idle | sending | sent | failed
const line = computed(() => ({
  sent: `We've emailed a reset link to ${masked(props.email)}`,
  failed: "Couldn't send — try again.",
})[reset.value] ?? "We'll email you a link to set a new one.");

async function sendReset() {
  if (reset.value === 'sending') return;
  reset.value = 'sending';
  try {
    await sendPasswordReset(props.email);
    reset.value = 'sent';
  } catch {
    reset.value = 'failed';
  }
}
</script>

<template>
  <section data-testid="details-card-signing-in">
    <div class="pdet-auth__sh"><h2>Signing in</h2><i class="pdet-auth__ln" /></div>

    <div class="pdet-auth__card">
      <div class="pdet-auth__row" data-testid="details-password">
        <div class="pdet-auth__t">
          <b>Password</b>
          <span aria-live="polite" :class="{ 'pdet-auth__failed': reset === 'failed' }">{{ line }}</span>
        </div>
        <XlButton variant="ghost" size="sm" :aria-disabled="reset === 'sending' ? 'true' : undefined" @click="sendReset">Change</XlButton>
      </div>

      <div class="pdet-auth__row" data-testid="details-two-step">
        <div class="pdet-auth__t">
          <b>Extra sign-in check</b>
          <span>A code when you sign in on a new device</span>
        </div>
        <XlTag>Coming soon</XlTag>
      </div>
    </div>

    <XlButton variant="ghost" class="pdet-auth__out" @click="router.push('/logout')">Sign out</XlButton>
  </section>
</template>

<style scoped>
/* Measurements from the prototype's profile() Signing in card, .row and .btn.g.blk, build 30 Sep 12:40. */
.pdet-auth__sh { display: flex; align-items: baseline; gap: 12px; margin: 34px 0 14px; }
.pdet-auth__sh h2 { margin: 0; font-size: 13px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--xl-color-outline-variant); }
.pdet-auth__ln { flex: 1; height: 1px; background: var(--xl-color-border); }
.pdet-auth__card {
  padding: 8px 22px;
  margin: 0 0 14px;
  background: #fff;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-1);
}
.pdet-auth__row { display: flex; align-items: center; gap: 14px; padding: 15px 0; border-bottom: 1px solid var(--xl-color-border-subtle); }
.pdet-auth__row:last-child { border-bottom: 0; }
.pdet-auth__t { flex: 1; min-width: 0; }
.pdet-auth__t b { display: block; font-size: 15px; }
.pdet-auth__t span { font-size: 13px; color: var(--xl-color-outline); overflow-wrap: anywhere; }
.pdet-auth__t .pdet-auth__failed { color: var(--xl-color-error); }
.pdet-auth__row :deep(.xl-btn[aria-disabled="true"]) { opacity: 0.7; cursor: progress; transform: none; box-shadow: none; }
/* The card's 14px margin plus the prototype's 16px spacer, above a full-width ghost button. */
.pdet-auth__out { width: 100%; margin-top: 16px; }
@media (max-width: 480px) { .pdet-auth__card { padding: 4px 14px; } }
</style>
